<LayoutMain>
  <template #header>
    <div class="serene-header">
      <Button variant="ghost" size="icon" @click="$router.back()" class="back-button">
        <ChevronLeft class="h-5 w-5" />
      </Button>
    </div>
  </template>

  <!-- Loading State -->
  <div v-if="loading" class="serene-loading">
    <div class="loading-content">
      <Text color="muted" class="loading-text">{{ t('userProfile.loading') }}</Text>
    </div>
  </div>

  <!-- Error State -->
  <div v-else-if="error" class="serene-error">
    <Card variant="elevated" class="error-card">
      <Text color="muted">{{ error }}</Text>
      <Button variant="outline" @click="loadUserProfile" class="mt-6">
        {{ t('userProfile.tryAgain') }}
      </Button>
    </Card>
  </div>

  <!-- Profile Content -->
  <div v-else class="serene-profile">
    
    <!-- Page Header with Avatar -->
    <div class="profile-header">
      <div class="header-content">
        <div class="header-main">
          <!-- Profile Avatar -->
          <div class="avatar-container">
            <img
              :src="mainPhotoUrl"
              :alt="user.name"
              class="profile-avatar"
              @click="openPhotoViewer(0)"
            />
            <!-- Status indicator on avatar -->
            <div v-if="isExploring" class="avatar-status exploring">
              <span class="status-dot"></span>
            </div>
            <div v-else-if="isOnline" class="avatar-status online">
              <span class="status-dot"></span>
            </div>
          </div>
          
          <!-- Name and Info -->
          <div class="header-info">
            <!-- Editable Name -->
            <div v-if="!isEditing || !isOwnProfile">
              <h1 class="profile-name">{{ user.name }}</h1>
            </div>
            <div v-else class="space-y-2">
              <Input
                v-model="editForm.name"
                type="text"
                placeholder="Your name"
                :error="!!nameError"
                maxlength="50"
                @blur="validateName"
                @input="clearNameError"
                class="text-2xl font-bold bg-white/90 backdrop-blur border-forest-primary/20"
              />
              <div v-if="nameError" class="text-red-600 text-sm bg-red-50 rounded px-2 py-1">
                {{ nameError }}
              </div>
            </div>
            <div class="header-details">
              <!-- Editable Pronouns -->
              <div v-if="!isEditing || !isOwnProfile">
                <span v-if="user.pronouns" class="pronouns">{{ user.pronouns }}</span>
              </div>
              <div v-else class="flex-grow">
                <Input
                  v-model="editForm.pronouns"
                  type="text"
                  placeholder="Pronouns (e.g., they/them, she/her, he/him)"
                  maxlength="30"
                  class="text-lg bg-white/90 backdrop-blur border-forest-primary/20"
                />
              </div>
              
              <!-- Status text -->
              <span v-if="isExploring" class="status-text exploring">exploring</span>
              <span v-else-if="isOnline" class="status-text online">online</span>
              
              <!-- Edit/Save/Cancel buttons for own profile -->
              <div v-if="isOwnProfile" class="flex items-center gap-2">
                <button 
                  v-if="!isEditing"
                  @click="enterEditMode"
                  class="header-edit"
                  :aria-label="t('userProfile.editProfile')"
                >
                  <Edit class="edit-icon" />
                </button>
                <div v-else class="flex items-center gap-2">
                  <button
                    @click="saveChanges"
                    :disabled="!isFormValid || isSaving"
                    class="header-save"
                    :aria-label="isSaving ? 'Saving...' : 'Save changes'"
                  >
                    <Save v-if="!isSaving" class="save-icon" />
                    <Loader2 v-else class="save-icon animate-spin" />
                  </button>
                  <button
                    @click="cancelEdit"
                    :disabled="isSaving"
                    class="header-cancel"
                    aria-label="Cancel editing"
                  >
                    <X class="cancel-icon" />
                  </button>
                </div>
              </div>
              
              <!-- Subtle appreciation heart -->
              <button 
                v-if="!isOwnProfile"
                @click="toggleLike"
                class="header-heart"
                :class="{ 'is-liked': isLiked }"
                :aria-label="isLiked ? 'Remove appreciation' : 'Show appreciation'"
              >
                <Heart class="heart-icon" :class="{ 'fill-current': isLiked }" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- About Section -->
    <!-- Photo Upload Section (onboarding only) -->
    <Card v-if="isEditing && isOwnProfile && isOnboarding" variant="elevated" class="content-card photo-upload-card">
      <div class="content-header">
        <h2 class="section-title">Your photo</h2>
        <span class="text-sm text-gray-500 ml-auto">Required</span>
      </div>
      <div class="content-body">
        <PhotoUploadSimple
          :current-photo-url="editPhotoUrl"
          :photos="userPhotos"
          :allow-multiple="!isOnboarding"
          @photo-uploaded="handlePhotoUploaded"
        />
      </div>
    </Card>

    <Card v-if="user.bio || (isEditing && isOwnProfile)" variant="elevated" class="content-card about-card">
      <div class="content-header">
        <h2 class="section-title">About {{ user.name }}</h2>
        <span v-if="isEditing && isOwnProfile" class="text-sm text-gray-500 ml-auto">
          Optional • {{ editForm.bio?.length || 0 }}/500
        </span>
      </div>
      <div class="content-body">
        <!-- View Mode -->
        <div v-if="!isEditing || !isOwnProfile" class="bio-text">
          <p v-for="(paragraph, index) in bioLines" :key="index" class="bio-paragraph">
            {{ paragraph }}
          </p>
        </div>
        <!-- Edit Mode -->
        <div v-else>
          <Textarea
            v-model="editForm.bio"
            placeholder="Tell others about yourself..."
            :error="!!bioError"
            maxlength="500"
            rows="4"
            @blur="validateBio"
            @input="clearBioError"
            class="w-full"
          />
          <div v-if="bioError" class="text-red-600 text-sm mt-2">{{ bioError }}</div>
        </div>
      </div>
    </Card>

    <!-- What Brings Joy Section -->
    <Card v-if="user.play_preferences || (isEditing && isOwnProfile)" variant="elevated" class="content-card joy-card">
      <div class="content-header">
        <h2 class="section-title">What brings joy</h2>
        <span v-if="isEditing && isOwnProfile" class="text-sm text-gray-500 ml-auto">
          Optional • {{ editForm.play_preferences?.length || 0 }}/500
        </span>
      </div>
      <div class="content-body">
        <!-- View Mode -->
        <div v-if="!isEditing || !isOwnProfile" class="joy-text">
          <p v-for="(paragraph, index) in joyLines" :key="index" class="joy-paragraph">
            {{ paragraph }}
          </p>
        </div>
        <!-- Edit Mode -->
        <div v-else>
          <Textarea
            v-model="editForm.play_preferences"
            placeholder="Share your play preferences and what you're looking for..."
            :error="!!playPreferencesError"
            maxlength="500"
            rows="4"
            @blur="validatePlayPreferences"
            @input="clearPlayPreferencesError"
            class="w-full"
          />
          <div v-if="playPreferencesError" class="text-red-600 text-sm mt-2">{{ playPreferencesError }}</div>
        </div>
      </div>
    </Card>

    <!-- Extended Presence Section -->
    <Card v-if="user.fetlife_username || (isEditing && isOwnProfile)" variant="elevated" class="content-card presence-card">
      <div class="content-header">
        <h2 class="section-title">Extended presence</h2>
        <span v-if="isEditing && isOwnProfile" class="text-sm text-gray-500 ml-auto">
          Optional
        </span>
      </div>
      <div class="content-body">
        <!-- View Mode -->
        <div v-if="!isEditing || !isOwnProfile">
          <p class="presence-description">
            Discover more about {{ user.name }}'s journey and interests on FetLife
          </p>
          <div class="presence-link">
            <a
              :href="`https://fetlife.com/${user.fetlife_username}`"
              target="_blank"
              rel="noopener noreferrer"
              class="fetlife-link"
            >
              {{ user.fetlife_username }}
              <ExternalLink class="h-4 w-4 ml-2" />
            </a>
          </div>
        </div>
        <!-- Edit Mode -->
        <div v-else class="space-y-3">
          <div class="flex items-center gap-2">
            <span class="text-gray-500 text-sm">fetlife.com/</span>
            <Input
              v-model="editForm.fetlife_username"
              type="text"
              placeholder="your_username"
              :error="!!fetlifeUsernameError"
              maxlength="50"
              class="flex-1"
              @input="validateFetLifeUsername"
            />
          </div>
          <div v-if="fetlifeUsernameError" class="text-red-600 text-sm">
            {{ fetlifeUsernameError }}
          </div>
          <p class="text-gray-600 text-sm">
            Connect your FetLife profile for others to learn more about your interests
          </p>
        </div>
      </div>
    </Card>

    <!-- Moments Captured Section - Now at bottom -->
    <Card v-if="userPhotos.length > 0 || (isOwnProfile && !isOnboarding)" class="content-card photos-card">
      <div class="content-header">
        <h2 class="section-title">Moments captured</h2>
        <span class="photo-count">({{ userPhotos.length }})</span>
        <Button
          v-if="isOwnProfile && !isEditingPhotos"
          variant="outline"
          size="sm"
          class="ml-auto"
          @click="enterPhotoEditMode"
        >
          <Edit class="h-4 w-4 mr-2" />
          Edit photos
        </Button>
        <Button
          v-else-if="isOwnProfile && isEditingPhotos"
          variant="outline"
          size="sm"
          class="ml-auto"
          @click="exitPhotoEditMode"
        >
          Done
        </Button>
      </div>
      <div class="content-body">
        <PhotoUploadSimple
          v-if="isOwnProfile && isEditingPhotos"
          :current-photo-url="editPhotoUrl"
          :photos="userPhotos"
          :allow-multiple="true"
          @photo-uploaded="handlePhotoUploaded"
        />
        <div v-else-if="userPhotos.length > 0" class="photo-gallery">
          <div
            v-for="(photo, idx) in userPhotos"
            :key="photo.id"
            class="gallery-item"
            @click="openPhotoViewer(idx)"
          >
            <img
              :src="photo.photo_url"
              :alt="`Photo ${idx + 1}`"
              class="gallery-photo"
            />
          </div>
        </div>
        <div v-else class="text-center text-gray-500">
          <p class="mb-4">No photos shared yet.</p>
          <Button variant="primary" @click="enterPhotoEditMode">
            <Camera class="h-4 w-4 mr-2" />
            Add photos
          </Button>
        </div>
      </div>
    </Card>

  </div>

  <!-- Full Screen Photo Viewer -->
  <Teleport to="body">
    <div v-if="viewerOpen" class="photo-viewer" @click="closeViewer">
      <!-- Close button -->
      <Button
        variant="ghost"
        size="icon"
        class="viewer-close"
        @click="closeViewer"
        :aria-label="t('userProfile.closePhotoViewer')"
      >
        <X class="h-6 w-6" />
      </Button>

      <!-- Photo display -->
      <div class="viewer-content" @click.stop>
        <img
          :src="currentPhoto?.photo_url"
          :alt="`Photo ${currentIndex + 1}`"
          class="viewer-photo"
        />

        <!-- Navigation -->
        <div v-if="userPhotos.length > 1" class="viewer-nav">
          <Button
            variant="ghost"
            size="icon"
            @click="previousPhoto"
            :disabled="currentIndex === 0"
            class="nav-btn"
            :aria-label="t('userProfile.previousPhoto')"
          >
            <ChevronLeft class="h-8 w-8" />
          </Button>

          <span class="photo-counter">
            {{ currentIndex + 1 }} of {{ userPhotos.length }}
          </span>

          <Button
            variant="ghost"
            size="icon"
            @click="nextPhoto"
            :disabled="currentIndex === userPhotos.length - 1"
            class="nav-btn"
            :aria-label="t('userProfile.nextPhoto')"
          >
            <ChevronRight class="h-8 w-8" />
          </Button>
        </div>
      </div>
    </div>
  </Teleport>
</LayoutMain>
